import { useTemplateFields } from "@/hooks/useTemplateFields";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { AvailableFields } from "./AvailableFields";
import { FieldDisplay } from "./FieldDisplay";
import { useEffect } from "react";
import { log } from "@/utils/debug";

interface TemplateFieldManagerProps {
  templateId: string;
}

export const TemplateFieldManager = ({ templateId }: TemplateFieldManagerProps) => {
  const { templateFields, handleAddField, handleRemoveField, handleMoveField } = useTemplateFields({ templateId });

  // For debugging purposes
  useEffect(() => {
    if (templateFields?.length) {
      log.process(
        `[TemplateFieldManager] Fields in order:`,
        templateFields.map((tf) => ({ id: tf.id, name: tf.field?.name, order: tf.order_index })),
      );
    }
  }, [templateFields]);

  const { data: fields } = useQuery({
    queryKey: ["fields"],
    queryFn: async () => {
      const { data, error } = await supabase.from("fields").select("id, name").order("name");

      if (error) throw error;
      return data;
    },
  });

  // Make sure we always sort templateFields by order_index for display
  const sortedTemplateFields = [...(templateFields || [])].sort((a, b) => a.order_index - b.order_index);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium">Template Fields</h3>
        <p className="text-sm text-muted-foreground">Add and arrange fields for this template</p>
      </div>

      <div className="space-y-2">
        {sortedTemplateFields.map((templateField, index) => (
          <FieldDisplay
            key={`${templateField.id}-${templateField.order_index}`}
            onMoveUp={() => handleMoveField(templateField.id, templateField.order_index - 1)}
            onMoveDown={() => handleMoveField(templateField.id, templateField.order_index + 1)}
            onRemove={() => handleRemoveField(templateField.id)}
            isFirst={index === 0}
            isLast={index === sortedTemplateFields.length - 1}
            templateId={templateId}
            availableFields={[]} // Passing empty array for now as it's not used
            templateField={templateField}
          />
        ))}
      </div>

      <AvailableFields
        fields={fields}
        onAddField={handleAddField}
        disabledFields={sortedTemplateFields.map((tf) => tf.field_id)}
      />
    </div>
  );
};
